import dayjs from 'dayjs';
import { formatDate } from '../../../utils/time.util';
import {
	TRACKING_ETA_MIN_SPEED_KMH,
	TRACKING_STEPS,
} from './orderTracking.constants';
import {
	TrackingDocumentEntry,
	TrackingDriverNotice,
	TrackingLatLng,
	TrackingLocationStatus,
	TrackingOrder,
	TrackingRouteMode,
	TrackingStepKey,
	TrackingStepView,
	TrackingTruck,
} from './orderTracking.interface';

export const reachedStepsCount = (truck: TrackingTruck): number =>
	truck.steps.filter((step) => step.completed).length;

export const isTruckComplete = (truck: TrackingTruck): boolean =>
	reachedStepsCount(truck) >= TRACKING_STEPS.length;

export const currentStepIndex = (truck: TrackingTruck): number => {
	const reached = reachedStepsCount(truck);
	return reached >= TRACKING_STEPS.length ? TRACKING_STEPS.length : reached - 1;
};

export const buildStepViews = (
	truck: TrackingTruck,
	translate: (key: string, defaultValue: string) => string,
): TrackingStepView[] => {
	const current = currentStepIndex(truck);

	return TRACKING_STEPS.map((step, index) => {
		const data = truck.steps.find((item) => item.key === step.key);
		return {
			key: step.key,
			label: translate(step.labelKey, step.defaultLabel),
			done: index < current,
			current: index === current,
			upcoming: index > current,
			completedAt: data?.completedAt,
			actor: data?.actor,
			documentsCount: data?.documents?.length ?? 0,
		};
	});
};

/** Great-circle distance between two points. */
export const distanceKm = (a: TrackingLatLng, b: TrackingLatLng): number => {
	const rad = (value: number) => (value * Math.PI) / 180;
	const dLat = rad(b.lat - a.lat);
	const dLng = rad(b.lng - a.lng);
	const h =
		Math.sin(dLat / 2) ** 2 +
		Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
	return 6371 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
};

const pathKm = (path: TrackingLatLng[]): number =>
	path.reduce(
		(sum, point, index) =>
			index ? sum + distanceKm(path[index - 1], point) : sum,
		0,
	);

/**
 * What is left of `path` for a truck at `from`: from the point of the path
 * nearest to it on, so the road ahead shrinks with every fix instead of in
 * steps from one route vertex to the next, and a route fetched a while ago
 * still measures the road ahead of the truck now.
 */
export const pathKmFrom = (
	path: TrackingLatLng[],
	from: TrackingLatLng,
): number => {
	if (!path.length) return 0;
	if (path.length === 1) return distanceKm(from, path[0]);
	// a flat projection around the truck is plenty at this scale
	const kmPerLat = 110.574;
	const kmPerLng = 111.32 * Math.cos((from.lat * Math.PI) / 180);
	const toXY = (p: TrackingLatLng) => ({
		x: (p.lng - from.lng) * kmPerLng,
		y: (p.lat - from.lat) * kmPerLat,
	});
	let nearest = { index: 1, point: path[0], distance: Infinity };
	for (let i = 1; i < path.length; i++) {
		const a = toXY(path[i - 1]);
		const b = toXY(path[i]);
		const dx = b.x - a.x;
		const dy = b.y - a.y;
		const length = dx * dx + dy * dy;
		const t = length
			? Math.max(0, Math.min(1, -(a.x * dx + a.y * dy) / length))
			: 0;
		const x = a.x + t * dx;
		const y = a.y + t * dy;
		const distance = Math.hypot(x, y);
		if (distance < nearest.distance) {
			nearest = {
				index: i,
				point: {
					lat: path[i - 1].lat + (path[i].lat - path[i - 1].lat) * t,
					lng: path[i - 1].lng + (path[i].lng - path[i - 1].lng) * t,
				},
				distance,
			};
		}
	}
	return (
		nearest.distance +
		distanceKm(nearest.point, path[nearest.index]) +
		pathKm(path.slice(nearest.index))
	);
};

/**
 * When a truck that was `km` away at its fix (`from`, ISO) gets there at
 * `speedKmh`. Nothing below the speed that says anything about the arrival.
 */
export const liveArrival = (
	from: string | undefined,
	km: number,
	speedKmh: number | undefined,
): string | undefined => {
	const at = from ? dayjs(from) : undefined;
	if (
		!at?.isValid() ||
		!(km >= 0) ||
		!speedKmh ||
		speedKmh < TRACKING_ETA_MIN_SPEED_KMH
	) {
		return undefined;
	}
	return at.add((km / speedKmh) * 3600 * 1000, 'millisecond').toISOString();
};

export const withoutLiveEta = <T extends TrackingTruck>(truck: T): T => ({
	...truck,
	liveEta: undefined,
	liveEtaTo: undefined,
	liveEtaKm: undefined,
});

export const withLiveTrip = <T extends TrackingTruck>(
	truck: T,
	order: Pick<TrackingOrder, 'route' | 'routeKnown'>,
	mode: TrackingRouteMode,
): T => {
	const base = withoutLiveEta(truck);
	const target = liveLegTarget(truck);
	if (!target || !truck.position || truck.signalLost) return base;

	if (target === 'pickup' ? mode === 'toDelivery' : mode === 'toPickup') {
		return base;
	}
	const planned = truck.route ?? (order.routeKnown ? order.route : undefined);
	const road =
		target === 'pickup' ? truck.approachRoute : truck.deliveryRoute ?? planned;
	if (!road?.length) return base;
	const km = pathKmFrom(road, truck.position);
	return {
		...base,
		liveEtaTo: target,
		liveEtaKm: km,
		liveEta: liveArrival(truck.positionAt, km, truck.liveSpeedKmh),
	};
};

export const toTuple = (point: TrackingLatLng): [number, number] => [
	point.lat,
	point.lng,
];

const cumulativeDistances = (route: TrackingLatLng[]): number[] => {
	const distances = [0];
	for (let i = 1; i < route.length; i++) {
		const a = route[i - 1];
		const b = route[i];
		distances.push(distances[i - 1] + Math.hypot(b.lat - a.lat, b.lng - a.lng));
	}
	return distances;
};

const pointAlong = (
	route: TrackingLatLng[],
	progress: number,
): { point: TrackingLatLng; index: number } => {
	if (!route.length) {
		return { point: { lat: 0, lng: 0 }, index: 0 };
	}
	const distances = cumulativeDistances(route);
	const target = distances[distances.length - 1] * progress;

	for (let i = 1; i < route.length; i++) {
		if (distances[i] >= target) {
			const ratio =
				(target - distances[i - 1]) / (distances[i] - distances[i - 1] || 1);
			const a = route[i - 1];
			const b = route[i];
			return {
				point: {
					lat: a.lat + (b.lat - a.lat) * ratio,
					lng: a.lng + (b.lng - a.lng) * ratio,
				},
				index: i,
			};
		}
	}

	return { point: route[route.length - 1], index: route.length - 1 };
};

export const truckPosition = (
	truck: TrackingTruck,
	route: TrackingLatLng[],
): TrackingLatLng =>
	truck.position ?? pointAlong(route, truck.routeProgress).point;

export const routeCompleted = (
	route: TrackingLatLng[],
	progress: number,
): TrackingLatLng[] => {
	const { point, index } = pointAlong(route, progress);
	return route.slice(0, index).concat([point]);
};

export const routeRemaining = (
	route: TrackingLatLng[],
	progress: number,
): TrackingLatLng[] => {
	const { point, index } = pointAlong(route, progress);
	return [point].concat(route.slice(index));
};

export const formatTime = (value?: string): string =>
	value ? dayjs(value).format('HH:mm') : '';

export const formatDay = (value?: string): string => formatDate(value);

export const formatDateTime = (value?: string): string =>
	value ? dayjs(value).format('DD.MM.YYYY — HH:mm') : '';

export const minutesSince = (value?: string): number =>
	value ? Math.max(0, dayjs().diff(dayjs(value), 'minute')) : 0;

export const formatDuration = (minutes: number): string => {
	if (minutes < 60) return `${minutes} min`;
	const hours = Math.floor(minutes / 60);
	const rest = minutes % 60;
	return rest ? `${hours} h ${rest} min` : `${hours} h`;
};

export const clampProgress = (value: number): number =>
	Math.min(1, Math.max(0, value || 0));

export const stepCompletedAt = (
	truck: TrackingTruck,
	key: TrackingStepKey,
): string | undefined =>
	truck.steps.find((step) => step.key === key)?.completedAt;

export const stepCompleted = (
	truck: TrackingTruck,
	key: TrackingStepKey,
): boolean => !!truck.steps.find((step) => step.key === key)?.completed;

export const stageStatusFromTrucks = (
	trucks: TrackingTruck[],
	key: TrackingStepKey,
): TrackingLocationStatus => {
	const reached = trucks.filter((truck) => stepCompleted(truck, key));
	if (trucks.length && reached.length === trucks.length) return 'completed';
	if (reached.length) return 'in_progress';
	return 'scheduled';
};

export const truckDocuments = (
	truck: TrackingTruck,
	steps: TrackingStepKey[],
): TrackingDocumentEntry[] =>
	truck.steps
		.filter((step) => steps.includes(step.key))
		.flatMap((step) =>
			(step.documents ?? []).map((doc) => ({
				...doc,
				uploadedAt: doc.uploadedAt ?? step.completedAt,
			})),
		);

export const totalDocuments = (trucks: TrackingTruck[]): number =>
	trucks.reduce(
		(sum, truck) =>
			sum +
			truck.steps.reduce(
				(count, step) => count + (step.documents?.length ?? 0),
				0,
			),
		0,
	);

export const isTruckAssigned = (truck: TrackingTruck): boolean =>
	!!(truck.plate || truck.driverName);

export const isTruckInTransit = (truck: TrackingTruck): boolean =>
	reachedStepsCount(truck) > 0 && !isTruckComplete(truck);

export const truckIdentity = (truck: TrackingTruck): string =>
	[truck.driverName, truck.plate].filter(Boolean).join(' · ');

/**
 * The end of the trip the truck is driving to right now: the pickup until it
 * is loaded, then the delivery until it is unloaded. Nothing once unloaded or
 * while the truck has no position to route from.
 */
export const liveLegTarget = (
	truck: TrackingTruck,
): 'pickup' | 'delivery' | undefined => {
	if (!truck.position) return undefined;
	if (!stepCompleted(truck, 'loaded')) return 'pickup';
	if (!stepCompleted(truck, 'unloaded')) return 'delivery';
	return undefined;
};

export const showTruckOnMap = (truck: TrackingTruck): boolean =>
	truck.onMap !== false &&
	(!!truck.position || stepCompleted(truck, 'to_loading'));

export const truckOnMap = (
	order: TrackingOrder,
	truck: TrackingTruck,
): boolean =>
	!order.positionsPending &&
	showTruckOnMap(truck) &&
	(!!truck.position || !!truck.route || order.routeKnown);

export const driverNotice = (
	order: TrackingOrder,
	truck: TrackingTruck,
	mode: TrackingRouteMode,
): TrackingDriverNotice | undefined => {
	if (order.positionsPending || isTruckComplete(truck)) return undefined;
	if (truckOnMap(order, truck)) {
		if (!truck.position) return { key: 'waitingForDriver', onMarker: true };
		if (truck.signalLost) return { key: 'liveDataPaused', onMarker: false };
		return undefined;
	}
	const key = !isTruckAssigned(truck)
		? 'noDriver'
		: truck.onMap === false
		? mode === 'toPickup'
			? 'leftPickup'
			: 'awaitingPickup'
		: !stepCompleted(truck, 'to_loading')
		? 'notStarted'
		: 'waitingForDriver';
	return { key, onMarker: false };
};

/**
 * When the driver app loses its connection, the next update may only carry
 * the driver's stored location (often the fleet base); the truck stays where
 * it was last seen instead, until a new GPS fix moves it on.
 */
export const holdLastSeenPositions = (
	previous: TrackingOrder | undefined,
	next: TrackingOrder,
): TrackingOrder => {
	if (!previous) return next;
	let held = false;
	const trucks = next.trucks.map((truck) => {
		if (
			truck.positionSource === 'gps' ||
			truck.onMap === false ||
			!isTruckInTransit(truck)
		) {
			return truck;
		}
		const before = previous.trucks.find((item) => item.id === truck.id);
		if (!before?.position || before.positionSource !== 'gps') return truck;
		held = true;
		return {
			...truck,
			position: before.position,
			positionAt: before.positionAt,
			lastSeenAt: before.lastSeenAt,
			routeProgress: before.routeProgress,
			positionSource: 'gps' as const,
			signalLost: true,
		};
	});
	return held ? { ...next, trucks } : next;
};

/** An arrival time, with the day once it is not today. */
export const formatArrival = (value?: string): string =>
	value && !dayjs(value).isSame(dayjs(), 'day')
		? dayjs(value).format('DD MMMM, HH:mm')
		: formatTime(value);

/** Time of the last fix, with the date once it is not from today. */
export const formatLastSeen = (value?: string): string =>
	value && !dayjs(value).isSame(dayjs(), 'day')
		? formatDateTime(value)
		: formatTime(value);
